import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";

export type UserRole = "student" | "teacher" | "admin";

export const useUserAllowedRoles = () => {
  const [allowedRoles, setAllowedRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Cache user ID and roles to prevent unnecessary re-fetches
  const userIdRef = useRef<string | null>(null);
  const cachedRolesRef = useRef<UserRole[]>([]);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    const fetchAllowedRoles = async () => {
      const currentUserId = user?.id || null;

      // If user is null, clear everything
      if (!currentUserId) {
        if (userIdRef.current !== null || !isInitializedRef.current) {
          userIdRef.current = null;
          cachedRolesRef.current = [];
          setAllowedRoles([]);
          setLoading(false);
          isInitializedRef.current = true;
        }
        return;
      }

      // If user ID hasn't changed, use cached data
      if (currentUserId === userIdRef.current && isInitializedRef.current) {
        setAllowedRoles(cachedRolesRef.current);
        setLoading(false);
        return;
      }

      // Only fetch if user ID changed or first time
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from("user_allowed_roles")
          .select("role")
          .eq("user_id", currentUserId);

        if (fetchError) {
          console.error("Error fetching allowed roles:", fetchError);
          setError(fetchError.message);
          // Still mark as initialized to prevent loops
          isInitializedRef.current = true;
          setLoading(false);
          return;
        }

        const roles = data?.map((item) => item.role as UserRole) || [];

        // Cache the results
        userIdRef.current = currentUserId;
        cachedRolesRef.current = roles;
        setAllowedRoles(roles);
        isInitializedRef.current = true;
      } catch (err) {
        console.error("Error fetching allowed roles:", err);
        setError("Failed to fetch allowed roles");
        // Still mark as initialized to prevent loops
        isInitializedRef.current = true;
      } finally {
        setLoading(false);
      }
    };

    fetchAllowedRoles();
  }, [user?.id]); // Only depend on user.id, not the full user object

  return {
    allowedRoles,
    loading,
    error,
  };
};
